import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield,
  Lock,
  MapPin,
  CheckCircle,
  XCircle,
  Database,
  Lightning,
  CloudCheck,
  ArrowLeft,
} from "@phosphor-icons/react";
import { KNORIA_LOGO_URL } from "../components/KnoriaLogo";
import { useLang } from "../contexts/LanguageContext";
import { PublicLangToggle } from "../components/PublicLangToggle";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

export default function ConfidentialiteIAPage() {
  const { tr } = useLang();
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="link-back-home">
            <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-9" />
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-white flex items-center gap-2" data-testid="link-back">
            <ArrowLeft size={16} /> Retour
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            {...fadeUp}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold uppercase tracking-wider mb-6"
            data-testid="badge-confidentiality"
          >
            <Shield size={14} /> {tr("Transparence radicale", "Radical transparency")}
          </motion.div>
          {/* Tâche 1401 — Sans bascule, la version anglaise de cette page
              serait inatteignable pour un visiteur : le sélecteur FR|EN vit
              derrière la connexion. Même raisonnement qu'en 1324. */}
          <div className="flex justify-center mb-4">
            <PublicLangToggle tone="dark" />
          </div>
          <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="hero-title">
            {tr("Confidentialité des données IA", "AI data privacy")}
          </motion.h1>
          <motion.p {...fadeUp} className="text-lg text-slate-400 max-w-2xl mx-auto">
            {tr(
              "Vous nous confiez les enregistrements de vos formations, les conversations avec vos clients, les questions de vos conseillers. Voici exactement ce qui se passe avec vos données.",
              "You trust us with the recordings of your training sessions, your client conversations and your advisers' questions. Here is exactly what happens to your data.",
            )}
          </motion.p>
        </div>
      </section>

      {/* Section 1 - Le flux des données */}
      <section className="px-6 py-12 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.h2 {...fadeUp} className="text-3xl font-bold mb-8 text-center" data-testid="section-flow-title">
            {tr("Comment vos données voyagent", "How your data travels")}
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Step 1 */}
            <motion.div
              {...fadeUp}
              className="relative bg-white/5 border border-white/10 rounded-2xl p-6"
              data-testid="card-step-storage"
            >
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#002FA7] flex items-center justify-center font-bold text-white">
                1
              </div>
              <Database size={28} className="text-emerald-400 mb-3" />
              <h3 className="font-bold text-lg mb-2">Stockage</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                {tr("Vos vidéos, vos transcriptions et toutes vos métadonnées sont stockées à", "Your videos, transcripts and all your metadata are stored in")}{" "}
                <strong className="text-white">{tr("Beauharnois, Québec", "Beauharnois, Quebec")}</strong>
                {tr(" chez OVHcloud Canada. Aucun fichier ne quitte le sol canadien.", " at OVHcloud Canada. No file ever leaves Canadian soil.")}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-300">
                <MapPin size={12} /> 🇨🇦 Canada uniquement
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              {...fadeUp}
              className="relative bg-white/5 border border-white/10 rounded-2xl p-6"
              data-testid="card-step-processing"
            >
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#002FA7] flex items-center justify-center font-bold text-white">
                2
              </div>
              <Lightning size={28} className="text-amber-400 mb-3" />
              <h3 className="font-bold text-lg mb-2">Traitement IA</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                {tr("Pour transcrire et extraire les connaissances, nous envoyons l'audio à", "To transcribe and extract the knowledge, we send the audio to")}
                <strong className="text-white"> OpenAI (Whisper, GPT-5.2)</strong>
                {tr(" via une API sécurisée. Le traitement dure quelques minutes.", " over a secure API. Processing takes a few minutes.")}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-300">
                <CloudCheck size={12} /> Traitement temporaire seulement
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              {...fadeUp}
              className="relative bg-white/5 border border-white/10 rounded-2xl p-6"
              data-testid="card-step-retention"
            >
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center font-bold text-white">
                3
              </div>
              <Lock size={28} className="text-emerald-400 mb-3" />
              <h3 className="font-bold text-lg mb-2">{tr("Zéro rétention IA", "Zero AI retention")}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                <strong className="text-emerald-300">{tr("OpenAI ne stocke pas vos données", "OpenAI does not store your data")}</strong>
                {tr(" et ne s'en sert pas pour entraîner ses modèles. Politique ", " and does not use it to train its models. ")}
                <strong className="text-white">Zero Data Retention</strong>
                {tr(" activée via notre intégration.", " policy enabled through our integration.")}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-300">
                <CheckCircle size={12} /> {tr("Aucune mémorisation", "Nothing memorised")}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2 - Ce qui EST / N'EST PAS fait */}
      <section className="px-6 py-12 bg-gradient-to-b from-transparent to-emerald-950/10 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.h2 {...fadeUp} className="text-3xl font-bold mb-8 text-center" data-testid="section-yes-no-title">
            Transparence totale : ce qui se passe (et ce qui ne se passe PAS)
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* CE QUI EST FAIT */}
            <motion.div
              {...fadeUp}
              className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6"
              data-testid="card-yes"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-emerald-300">
                <CheckCircle size={24} /> Ce qui EST fait
              </h3>
              <ul className="space-y-3 text-sm">
                {[
                  tr("Audio envoyé à OpenAI Whisper pour transcription", "Audio sent to OpenAI Whisper for transcription"),
                  tr("Transcription analysée par GPT-5.2 pour extraire les Q&R", "Transcript analysed by GPT-5.2 to extract the Q&A"),
                  tr("Vecteurs (embeddings) stockés dans MongoDB Beauharnois", "Vectors (embeddings) stored in MongoDB Beauharnois"),
                  tr("Audio analysé par AssemblyAI (Canada) pour identifier les locuteurs", "Audio analysed by AssemblyAI (Canada) to identify the speakers"),
                  tr("Toutes les Q&R, transcriptions et embeddings sauvegardés au Québec", "All Q&A, transcripts and embeddings saved in Quebec"),
                  tr("Accès strictement contrôlé par organisation (multi-tenant)", "Access strictly controlled per organisation (multi-tenant)"),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300">
                    <CheckCircle size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* CE QUI N'EST PAS FAIT */}
            <motion.div
              {...fadeUp}
              className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6"
              data-testid="card-no"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-red-300">
                <XCircle size={24} /> Ce qui N'EST PAS fait
              </h3>
              <ul className="space-y-3 text-sm">
                {[
                  tr("OpenAI N'utilise PAS vos données pour entraîner ses modèles", "OpenAI does NOT use your data to train its models"),
                  tr("OpenAI NE conserve PAS vos données après traitement (ZDR)", "OpenAI does NOT keep your data after processing (ZDR)"),
                  tr("Aucun fichier vidéo ou audio n'est stocké aux États-Unis", "No video or audio file is stored in the United States"),
                  tr("Aucune donnée n'est partagée avec des tiers (Google, Meta, etc.)", "No data is shared with third parties (Google, Meta, etc.)"),
                  tr("Aucun accès à votre IP ni à votre identité personnelle par les sous-traitants", "No access to your IP address or personal identity by our subprocessors"),
                  tr("Aucune publicité ni profilage commercial sur vos données", "No advertising or commercial profiling on your data"),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300">
                    <XCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3 - Sous-traitants */}
      <section className="px-6 py-12 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.h2 {...fadeUp} className="text-3xl font-bold mb-8 text-center" data-testid="section-subprocessors-title">
            {tr("Nos sous-traitants IA — pleinement divulgués", "Our AI subprocessors — fully disclosed")}
          </motion.h2>

          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Sous-traitant</th>
                  <th className="text-left px-5 py-3 font-semibold">Role</th>
                  <th className="text-left px-5 py-3 font-semibold">Localisation</th>
                  <th className="text-left px-5 py-3 font-semibold">Stockage ?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {[
                  { name: "OpenAI Whisper", role: "Transcription audio → texte", loc: "USA (traitement)", storage: false },
                  { name: "OpenAI GPT-5.2", role: "Extraction Q&R + Dialogueur", loc: "USA (traitement)", storage: false },
                  { name: "OpenAI Embeddings", role: "Vecteurs semantiques", loc: "USA (traitement)", storage: false },
                  { name: "AssemblyAI", role: "Identification des locuteurs", loc: "USA (traitement)", storage: false },
                  { name: "OVHcloud Canada", role: "Stockage videos + DB", loc: "🇨🇦 Beauharnois, QC", storage: true },
                  { name: "Stripe", role: "Paiements (PCI-DSS)", loc: "USA", storage: true },
                ].map((sp, i) => (
                  <tr key={i} className="hover:bg-white/5">
                    <td className="px-5 py-3 font-semibold">{sp.name}</td>
                    <td className="px-5 py-3 text-slate-400">{sp.role}</td>
                    <td className="px-5 py-3">{sp.loc}</td>
                    <td className="px-5 py-3">
                      {sp.storage ? (
                        <span className="text-emerald-300">✓ Oui</span>
                      ) : (
                        <span className="text-slate-500">✗ Non</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-xs text-slate-500 mt-4">
            Mise à jour : avril 2026 · Documente dans notre DPA (Data Processing Agreement)
          </p>
        </div>
      </section>

      {/* Section 4 - Compliance */}
      <section className="px-6 py-12 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 {...fadeUp} className="text-3xl font-bold mb-4" data-testid="section-compliance-title">
            {tr("Conformité légale", "Legal compliance")}
          </motion.h2>
          <motion.p {...fadeUp} className="text-slate-400 mb-8">
            {tr(
              "KNORIA est entièrement conforme aux exigences canadiennes et québécoises.",
              "KNORIA fully complies with Canadian and Quebec requirements.",
            )}
          </motion.p>
          <div className="flex flex-wrap justify-center gap-4">
            {["Loi 25 (Québec)", "LPRPDE / PIPEDA", "C.c.Q. art. 1474", "DPA signe", "ISO 27001 (OVH)", "SOC 2 (sous-traitants)"].map(
              (item) => (
                <div
                  key={item}
                  className="px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold"
                >
                  ✓ {item}
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-16 border-t border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">{tr("Des questions sur la confidentialité ?", "Questions about privacy?")}</h2>
          <p className="text-slate-400 mb-6">
            Notre Delegue a la protection des données (DPO) est joignable directement.
          </p>
          <a
            href="mailto:dpo@knoria.com"
            className="inline-block px-6 py-3 bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-lg font-semibold transition-colors"
            data-testid="btn-contact-dpo"
          >
            {tr("Contacter notre DPO", "Contact our DPO")}
          </a>
        </div>
      </section>

      <footer className="border-t border-white/10 px-6 py-6 text-center text-xs text-slate-500">
        © 2026 KNORIA Inc. (9439-7452 Québec Inc.) · Tous droits reserves
      </footer>
    </div>
  );
}
